import { TextAssist } from '../comments/TextAssist';
import { PhotoGuideRow } from './PhotoGuide';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { navigate } from '../../app/navigation';
import type { V2Evidence } from '../../domain/evidence';
import type { V2Area, V2Inspection } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { aiFailureMessage } from '../../i18n/labels';
import { aiBackendConfigured } from '../../backend/aiClient';
import { aiQuotaBlockFor, billingStore, ensureBillingStarted } from '../../state/billingStore';
import { AiBlockFix } from '../ai/AiBlockFix';
import { PurchaseSheet } from '../billing/PurchaseSheet';
import { aiPolicyBlockFor, aiStore, aiUnavailableReason, analyzedPhotoIdsOfArea, areaScanSummaryOf, latestRunOfArea, liveRunOfArea, reanalyzeAreaAction, runAreaAnalysisAction } from '../../state/aiStore';
import type { EvidenceTarget } from '../../state/captureFlow';
import { useStore } from '../../state/createStore';
import { setAreaAiSkippedAction, setAreaScanNoteAction } from '../../state/inspectionStore';
import { sessionStore } from '../../state/sessionStore';
import { Button, Card, ConfirmSheet, IconButton, InfoSheet, InlineMessage, SectionEyebrow, TextArea } from '../../ui/components';
import { formatClock, useAutosaveText, useOnlineStatus } from '../../ui/hooks';
import { Thumbnail } from '../evidence/PhotoStrip';
import { useEvidenceCapture } from '../evidence/useEvidenceCapture';

// AI 区域扫描（可选）(Owner task book 2026-09-04 Round 2 §F / §G / §H / §I / §R / §AH):
//   拍照 / 从相册选择 are visible at once — no "start scan" gate;
//   区域照片 stays compact: a count, up to three previews, 查看全部 (the full gallery is the pool);
//   区域整体备注 sits here, before AI 分析, and persists whether or not AI runs (one line while empty, 2026-10-28);
//   AI 分析 sends the REAL request (photos + photo notes + overall note + checklist context) to the
//   backend and shows the real states — preparing · uploading · analyzing · results · failed;
//   跳过 AI / 开始检查 always remains; a failure never blocks the manual flow.
const PREVIEW_COUNT = 3;

export function AiScanCard(props: {
  inspection: V2Inspection;
  area: V2Area;
  target: EvidenceTarget;
  photos: V2Evidence[];
  onOpenPhoto: (index: number) => void;
  onViewAll: () => void;
  disabled?: boolean;
}) {
  const { t, tp, locale } = useT();
  const online = useOnlineStatus();
  const sessionPhase = useStore(sessionStore, (s) => s.phase);
  const live = useStore(aiStore, (s) => liveRunOfArea(s, props.area.id));
  const lastRun = useStore(aiStore, (s) => latestRunOfArea(s, props.area.id));
  const aiState = useStore(aiStore, (s) => s);
  const { area, photos } = props;
  // 2026-10-28 (device walkthrough A8): 「已分析 N 张」 counts every photo of the area any finished analysis saw.
  const summary = useMemo(() => areaScanSummaryOf(aiState, props.area.id, photos.map((p) => p.id)), [aiState, props.area.id, photos]);
  const [helpOpen, setHelpOpen] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [dismissedRunId, setDismissedRunId] = useState<string | null>(null);
  const capture = useEvidenceCapture(props.target);
  const saveScanNote = useCallback((value: string) => setAreaScanNoteAction(area.id, value), [area.id]);
  const scanNote = useAutosaveText(area.scanNote, saveScanNote);
  // TestFlight 210 (2026-10-28): on an English iPhone the empty note (about 250 pt with its buttons and hint)
  // pushed 「全部重新分析」 ~150 pt under the area's sticky bar. Owner 2026-10-28: the note stays BEFORE AI 分析
  // (task book 2026-09-04), but while nothing is written it is one line, without 「存为评语」 and the hint; touched,
  // dictated into or filled from the library it opens, and stays open while the page is shown.
  const [noteOpened, setNoteOpened] = useState(false);
  const noteCompact = !noteOpened && scanNote.value.trim().length === 0;
  const busy = capture.busy !== null || props.disabled === true;
  // LAYER 1 (PP-21 / PP-20 / jurisdiction): a policy block is not a transient condition, so it is
  // reported first and the Analyze button is genuinely disabled rather than failing on tap.
  const policyBlock = aiPolicyBlockFor(props.inspection);
  // LAYER 2 (Owner D-4 / D-5): the allowance this account has left. The server decides every
  // request, but the card must not invite a run it already knows cannot be paid for.
  const quotaBlock = useStore(billingStore, () => aiQuotaBlockFor(props.inspection.id));
  const canBuyAddOn = useStore(billingStore, (s) => s.canBuyAddOn);
  // Idempotent: the entitlement may not have been read yet when the app opens straight into an area.
  useEffect(() => {
    void ensureBillingStarted();
  }, []);
  const unavailable = aiUnavailableReason({ photos: photos.length, online, signedIn: sessionPhase === 'signedIn', configured: aiBackendConfigured(), locked: props.disabled === true, busy: live !== null, policyBlock, quotaBlock });
  const failed = lastRun && lastRun.status === 'failed' && lastRun.id !== dismissedRunId ? lastRun : null;
  const done = lastRun && lastRun.status === 'done' ? lastRun : null;
  // Owner decision list 2026-10-19 item 29: from the second analysis on, only the photos no finished
  // analysis of this area has seen are sent, and their results are ADDED to the earlier ones. Running
  // every photo again is a separate, confirmed action, charged again.
  const analyzed = useMemo(() => analyzedPhotoIdsOfArea(aiState, props.area.id), [aiState, props.area.id]);
  const newSince = done ? photos.filter((p) => !analyzed.has(p.id)) : [];
  const [redoOpen, setRedoOpen] = useState(false);
  // 2026-10-28 (Owner: 扫描卡「分析完自动折成一行」): an analysed area's card was 687 px on a 375 × 667 phone — more than a
  // screen to scroll past every time the inspector came back to the rows. Once the analysis is done and nothing
  // is running or failed, the card is one line (what was analysed · 拍照 · 相册 · 展开). New photos put 「分析新增的
  // N 张照片」 on that line instead of opening the card: opened, the button sat under the bottom bar on entry (430 × 839,
  // English). When the AI cannot run (offline, quota, …) the card opens, since only the open card says why.
  const [expanded, setExpanded] = useState(false);
  const collapsible = done !== null && live === null && failed === null && (newSince.length === 0 || unavailable === null);

  async function analyze(photoIds?: string[]) {
    await scanNote.flush();
    // New photos only: their observations are added to what earlier runs found.
    await runAreaAnalysisAction(area.id, photoIds ? { photoIds, append: analyzed.size > 0 } : {});
  }

  // 「全部重新分析」, confirmed: every photo of the area, in as many requests as it takes (2026-10-28).
  async function analyzeEverything() {
    await scanNote.flush();
    await reanalyzeAreaAction(area.id);
  }

  const help = <IconButton icon="help" label={t('aiScan.helpLabel')} onClick={() => setHelpOpen(true)} tone="primary" testId="ai-scan-help" />;
  const helpSheet = helpOpen ? <InfoSheet title={t('aiScan.title')} paragraphs={[t('aiScan.help1'), t('aiScan.help2'), t('aiScan.help3')]} onClose={() => setHelpOpen(false)} testId="ai-scan-help-sheet" /> : null;

  if (area.aiSkipped) {
    return (
      <>
        <Card tone="low" className="ai-card ai-card-collapsed" testId="ai-card-collapsed">
          <span className="icon-tile">
            <span className="sparkle">✦</span>
          </span>
          <span className="ai-card-collapsed-text">{photos.length > 0 ? `${t('area.aiSkipped')} · ${tp('common.photosCount', photos.length)}` : t('area.aiSkipped')}</span>
          <Button variant="text" size="small" block={false} onClick={() => void setAreaAiSkippedAction(area.id, false)} disabled={props.disabled} testId="show-ai">
            {t('area.showAi')}
          </Button>
        </Card>
        <PhotoGuideRow area={area} target={props.target} disabled={props.disabled} />
        {helpSheet}
      </>
    );
  }

  const previews = photos.slice(-PREVIEW_COUNT).reverse();

  if (collapsible && !expanded && done && summary) {
    return (
      <>
        <Card tone="low" className="ai-card ai-card-done" testId="ai-scan-card-done">
          <div className="ai-card-done-line">
            <span className="icon-tile">
              <span className="sparkle">✦</span>
            </span>
            <p className="ai-card-collapsed-text" data-testid="ai-status-line" data-rows={summary.rows} data-unclassified={summary.unclassified}>
              {t('area.aiStatusLine', { photos: summary.photos, rows: summary.rows, unclassified: summary.unclassified })}
              {done.finishedAt !== null ? ` · ${formatClock(done.finishedAt)}` : ''}
            </p>
          </div>
          {newSince.length > 0 ? (
            <Button size="small" icon="add_a_photo" onClick={() => void analyze(newSince.map((p) => p.id))} disabled={unavailable !== null} testId="ai-analyze-new">
              {tp('area.aiNewPhotos', newSince.length)}
            </Button>
          ) : null}
          <div className="ai-card-done-actions" data-testid="ai-capture">
            <Button variant="tonal" size="small" block={false} icon="photo_camera" onClick={() => void capture.takePhoto()} disabled={busy} testId="btn-camera">
              {capture.busy === 'camera' ? t('photo.opening') : t('photo.takePhoto')}
            </Button>
            <Button variant="secondary" size="small" block={false} icon="photo_library" onClick={() => void capture.chooseLibrary()} disabled={busy} testId="btn-library">
              {capture.busy === 'library' ? t('photo.importing') : t('photo.chooseLibrary')}
            </Button>
            <Button variant="text" size="small" block={false} trailingIcon="keyboard_arrow_down" onClick={() => setExpanded(true)} testId="ai-scan-expand">
              {t('aiScan.expand')}
            </Button>
          </div>
          {capture.message ? <InlineMessage kind={capture.message.kind} text={capture.message.text} /> : null}
        </Card>
        {helpSheet}
      </>
    );
  }

  return (
    <>
      <Card tone="low" className="ai-card" testId="ai-scan-card">
        <div className="ai-card-head">
          <span className="icon-tile">
            <span className="sparkle">✦</span>
          </span>
          <div className="ai-card-heading">
            <SectionEyebrow tone="primary">{t('area.aiEyebrow')}</SectionEyebrow>
            <h3 className="ai-card-title">{t('aiScan.title')}</h3>
          </div>
          <div className="ai-card-head-actions">
            {collapsible ? (
              <Button variant="text" size="small" block={false} trailingIcon="keyboard_arrow_up" onClick={() => setExpanded(false)} testId="ai-scan-collapse">
                {t('aiScan.collapse')}
              </Button>
            ) : null}
            {help}
          </div>
        </div>

        <div className="btn-pair ai-capture" data-testid="ai-capture">
          <Button variant="tonal" icon="photo_camera" onClick={() => void capture.takePhoto()} disabled={busy} testId="btn-camera">
            {capture.busy === 'camera' ? t('photo.opening') : t('photo.takePhoto')}
          </Button>
          <Button variant="secondary" icon="photo_library" onClick={() => void capture.chooseLibrary()} disabled={busy} testId="btn-library">
            {capture.busy === 'library' ? t('photo.importing') : t('photo.chooseLibrary')}
          </Button>
        </div>
        {capture.message ? <InlineMessage kind={capture.message.kind} text={capture.message.text} /> : null}

        <section className="ai-photos" data-testid="area-photos" data-count={photos.length}>
          <div className="ai-photos-head">
            <SectionEyebrow>{`${t('area.photos')} · ${tp('common.photosCount', photos.length)}`}</SectionEyebrow>
            {photos.length > 0 ? (
              <Button variant="text" size="small" block={false} trailingIcon="chevron_right" onClick={props.onViewAll} testId="area-photos-all">
                {t('area.viewAllPhotos')}
              </Button>
            ) : null}
          </div>
          {photos.length === 0 ? (
            <p className="photo-empty">{t('aiScan.noPhotos')}</p>
          ) : (
            <div className="ai-photo-preview" data-scroll-x="true" data-testid="area-photo-preview">
              {previews.map((ev) => (
                <Thumbnail key={ev.id} evidence={ev} onClick={() => props.onOpenPhoto(photos.indexOf(ev))} />
              ))}
              {photos.length > PREVIEW_COUNT ? (
                <button type="button" className="ai-photo-more" onClick={props.onViewAll} data-testid="area-photos-more">
                  +{photos.length - PREVIEW_COUNT}
                </button>
              ) : null}
            </div>
          )}
          {/* Owner decision list 2026-10-19 item 31: the usual shots of this kind of area. */}
          <PhotoGuideRow area={area} target={props.target} disabled={busy} />
        </section>

        <div className="field" data-testid="area-scan-note-field">
          <div className="field-head">
            <label className="field-label" htmlFor="area-scan-note">
              {t('area.scanNote')}
            </label>
            <span className="field-required">{t('common.optional')}</span>
          </div>
          <TextArea id="area-scan-note" value={scanNote.value} onChange={scanNote.setValue} placeholder={t('area.scanNotePlaceholder')} rows={3} compact={noteCompact} onFocus={() => setNoteOpened(true)} disabled={props.disabled} testId="area-scan-note" />
          <TextAssist value={scanNote.value} onChange={scanNote.setValue} fieldId="area-scan-note" lang={locale} disabled={props.disabled} compact={noteCompact} testId="area-scan-note-assist" />
          {noteCompact ? null : <p className="saved-note">{scanNote.savedAt ? t('common.autosaved', { time: formatClock(scanNote.savedAt) }) : t('area.scanNoteHint')}</p>}
        </div>

        {live ? (
          <div className="ai-progress" data-testid="ai-progress" data-phase={live.phase}>
            <span className="ai-progress-spinner" aria-hidden="true" />
            <div className="ai-progress-main">
              <p className="ai-progress-title">
                {live.phase === 'preparing' ? t('area.aiPhase.preparing') : live.phase === 'uploading' ? t('area.aiPhase.uploading', { pct: live.uploadPct }) : tp('area.aiPhase.analyzing', photos.length)}
              </p>
              <p className="card-note">{t('area.aiPhaseHint')}</p>
            </div>
          </div>
        ) : failed ? (
          <div className="ai-failed" data-testid="ai-failed" data-code={failed.errorCode ?? ''}>
            <p className="ai-failed-title">{t('area.aiFailed')}</p>
            <p className="card-note">{aiFailureMessage(t, failed.errorCode)}</p>
            <div className="btn-pair">
              <Button icon="refresh" onClick={() => void analyze(analyzed.size > 0 ? failed.photoIds : undefined)} disabled={unavailable !== null && unavailable !== 'busy'} testId="ai-retry">
                {t('common.retry')}
              </Button>
              <Button variant="secondary" onClick={() => setDismissedRunId(failed.id)} testId="ai-continue-manually">
                {t('area.aiContinueManually')}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {done && summary ? (
              <p className="card-note ai-done-line" data-testid="ai-status-line" data-rows={summary.rows} data-unclassified={summary.unclassified}>
                {t('area.aiStatusLine', { photos: summary.photos, rows: summary.rows, unclassified: summary.unclassified })}
                {done.finishedAt !== null ? ` · ${formatClock(done.finishedAt)}` : ''}
              </p>
            ) : null}
            {newSince.length > 0 ? (
              <Button variant="secondary" icon="add_a_photo" onClick={() => void analyze(newSince.map((p) => p.id))} disabled={unavailable !== null} testId="ai-analyze-new">
                {tp('area.aiNewPhotos', newSince.length)}
              </Button>
            ) : null}
            <Button variant={done ? 'secondary' : 'primary'} onClick={() => (done ? setRedoOpen(true) : void analyze())} disabled={unavailable !== null} testId="analyze-area">
              <span className="sparkle-inline">✦</span> {done ? t('area.aiReanalyze') : tp('area.analyze', photos.length)}
            </Button>
            {unavailable !== null ? (
              <>
                <p className="card-note ai-card-reason" data-testid="ai-reason">
                  {aiFailureMessage(t, unavailable)}
                </p>
                {unavailable === 'quotaExhausted' ? (
                  <Button variant="secondary" icon="credit_card" onClick={() => (canBuyAddOn ? setPurchaseOpen(true) : navigate({ screen: 'settingsPage', page: 'plan' }))} testId="ai-quota-buy">
                    {canBuyAddOn ? t('billing.addOnCta') : t('billing.planCta')}
                  </Button>
                ) : null}
                {props.disabled !== true ? <AiBlockFix inspection={props.inspection} reason={unavailable} testId="ai-fix" /> : null}
              </>
            ) : (
              <p className="card-note ai-card-reason" data-testid="ai-privacy">
                {t('ai.privacy')}
              </p>
            )}
          </>
        )}

        <Button variant="text" size="small" onClick={() => void setAreaAiSkippedAction(area.id, true)} disabled={props.disabled} testId="skip-ai">
          {t('area.skipAi')}
        </Button>
      </Card>
      {helpSheet}
      {redoOpen ? (
        <ConfirmSheet
          title={t('area.aiRedoTitle')}
          body={tp('area.aiRedoBody', photos.length)}
          confirmLabel={t('area.aiRedoConfirm')}
          onConfirm={() => {
            setRedoOpen(false);
            void analyzeEverything();
          }}
          onCancel={() => setRedoOpen(false)}
          testId="ai-redo-confirm"
        />
      ) : null}
      {purchaseOpen ? <PurchaseSheet mode="addOn" onClose={() => setPurchaseOpen(false)} onPurchased={() => setPurchaseOpen(false)} /> : null}
    </>
  );
}
